import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, RefreshCw, Mail, Phone, Building2, MessageSquare, ChevronDown, X, Plus, ExternalLink, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Source badge colors
const SOURCE_COLORS = {
  'lestari.sg': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  'sustek.ai': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  'manual': { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
  'referral': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' }
};

// Status badge colors
const STATUS_COLORS = {
  'new': { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  'contacted': { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  'qualified': { bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-500' },
  'converted': { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  'closed': { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' }
};

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'converted', 'closed'];

function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service_interest: '',
    source: 'manual'
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (sourceFilter) params.append('source', sourceFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await fetch(`${API_URL}/api/crm/leads?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, statusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [fetchLeads, fetchStats]);

  // Update lead status
  const updateStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
        if (selectedLead?.id === leadId) {
          setSelectedLead(updated);
        }
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Create new lead
  const createLead = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/crm/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLead)
      });
      
      if (response.ok) {
        setShowAddModal(false);
        setNewLead({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          service_interest: '',
          source: 'manual'
        });
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to create lead:', error);
    }
  };

  // Delete lead
  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${leadId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setLeads(prev => prev.filter(l => l.id !== leadId));
        if (selectedLead?.id === leadId) {
          setSelectedLead(null);
        }
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to delete lead:', error);
    }
  };

  // Filter leads by search term
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(term) ||
      lead.email?.toLowerCase().includes(term) ||
      lead.company?.toLowerCase().includes(term)
    );
  });

  // Get unique sources from leads
  const uniqueSources = [...new Set(leads.map(l => l.source).filter(Boolean))];

  const getSourceColor = (source) => SOURCE_COLORS[source] || SOURCE_COLORS.default;
  const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.new;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="leads-management">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Leads Management</h1>
                <p className="text-xs text-slate-500">SusTek CRM</p>
              </div>
            </div>
            
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
              data-testid="add-lead-btn"
            >
              <Plus size={16} />
              Add Lead
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="leads-stats">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                <BarChart3 size={14} />
                Total Leads
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            
            {Object.entries(stats.by_source || {}).map(([source, count]) => {
              const colors = getSourceColor(source);
              return (
                <div key={source} className={`rounded-xl p-4 border shadow-sm ${colors.bg} ${colors.border}`}>
                  <div className={`text-xs mb-1 ${colors.text} opacity-80`}>{source}</div>
                  <p className={`text-2xl font-bold ${colors.text}`}>{count}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                data-testid="search-input"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors ${
                showFilters || sourceFilter || statusFilter
                  ? 'bg-teal-50 border-teal-200 text-teal-700'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="filter-toggle"
            >
              <Filter size={16} />
              Filters
              {(sourceFilter || statusFilter) && (
                <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center">
                  {(sourceFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Refresh */}
            <button
              onClick={() => { fetchLeads(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="px-4 pb-4 flex flex-wrap gap-3 border-t border-slate-100 pt-4">
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="source-filter"
              >
                <option value="">All Sources</option>
                {uniqueSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="status-filter"
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                ))}
              </select>

              {(sourceFilter || statusFilter) && (
                <button
                  onClick={() => { setSourceFilter(''); setStatusFilter(''); }}
                  className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Leads Table */}
          <div className={`flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${selectedLead ? 'hidden lg:block' : ''}`}>
            {loading ? (
              <div className="p-8 text-center text-slate-500">
                <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                Loading leads...
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <MessageSquare className="mx-auto mb-2 opacity-50" size={32} />
                <p>No leads found</p>
                <p className="text-sm mt-1">
                  {searchTerm || sourceFilter || statusFilter ? 'Try adjusting your filters' : 'Leads will appear here when submitted'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[550px]" data-testid="leads-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Lead</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLeads.map((lead) => {
                      const sourceColor = getSourceColor(lead.source);
                      const statusColor = getStatusColor(lead.status);
                      
                      return (
                        <tr
                          key={lead.id}
                          onClick={() => setSelectedLead(lead)}
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                            selectedLead?.id === lead.id ? 'bg-teal-50' : ''
                          }`}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900">{lead.name}</p>
                              <p className="text-sm text-slate-500">{lead.email}</p>
                              {lead.company && (
                                <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                  <Building2 size={12} />
                                  {lead.company}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${sourceColor.bg} ${sourceColor.text} ${sourceColor.border}`}>
                              {lead.source}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`}></span>
                              {lead.status?.charAt(0).toUpperCase() + lead.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 hidden sm:table-cell">
                            {formatDate(lead.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Lead Detail Panel */}
          {selectedLead && (
            <div className="w-full lg:w-96 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="lead-detail-panel">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Lead Details</h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={18} className="text-slate-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Name & Status */}
                <div>
                  <h4 className="text-lg font-semibold text-slate-900">{selectedLead.name}</h4>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getSourceColor(selectedLead.source).bg} ${getSourceColor(selectedLead.source).text} ${getSourceColor(selectedLead.source).border}`}>
                      {selectedLead.source}
                    </span>
                  </div>
                </div>

                {/* Status Selector */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Status</label>
                  <div className="relative mt-1">
                    <select
                      value={selectedLead.status}
                      onChange={(e) => updateStatus(selectedLead.id, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      data-testid="status-select"
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail size={16} className="text-slate-400" />
                    <a href={`mailto:${selectedLead.email}`} className="text-teal-600 hover:underline">
                      {selectedLead.email}
                    </a>
                  </div>
                  
                  {selectedLead.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone size={16} className="text-slate-400" />
                      <a href={`tel:${selectedLead.phone}`} className="text-slate-700">
                        {selectedLead.phone}
                      </a>
                    </div>
                  )}
                  
                  {selectedLead.company && (
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 size={16} className="text-slate-400" />
                      <span className="text-slate-700">{selectedLead.company}</span>
                    </div>
                  )}
                </div>

                {/* Service Interest */}
                {selectedLead.service_interest && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Service Interest</label>
                    <p className="mt-1 text-sm text-slate-700">{selectedLead.service_interest}</p>
                  </div>
                )}

                {/* Message */}
                {selectedLead.message && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Message</label>
                    <p className="mt-1 text-sm text-slate-700 bg-slate-50 rounded-lg p-3">{selectedLead.message}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="pt-4 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                  <p>Created: {formatDate(selectedLead.created_at)}</p>
                  <p>Updated: {formatDate(selectedLead.updated_at)}</p>
                  <p className="font-mono text-slate-400">ID: {selectedLead.id}</p>
                </div>

                {/* Actions */}
                <div className="pt-4 flex gap-2">
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    <Mail size={16} />
                    Send Email
                  </a>
                  <button
                    onClick={() => deleteLead(selectedLead.id)}
                    className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm"
                    data-testid="delete-lead-btn"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-lead-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Add New Lead</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={18} className="text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={createLead} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="new-lead-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="new-lead-email"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newLead.phone}
                    onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="new-lead-phone"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={newLead.company}
                    onChange={(e) => setNewLead({...newLead, company: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="new-lead-company"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Service Interest</label>
                <input
                  type="text"
                  value={newLead.service_interest}
                  onChange={(e) => setNewLead({...newLead, service_interest: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., ESG Reporting, Data Engineering"
                  data-testid="new-lead-service"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
                <select
                  value={newLead.source}
                  onChange={(e) => setNewLead({...newLead, source: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="new-lead-source"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="sustek.ai">SusTek Website</option>
                  <option value="referral">Referral</option>
                  <option value="event">Event</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea
                  rows={3}
                  value={newLead.message}
                  onChange={(e) => setNewLead({...newLead, message: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="new-lead-message"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                  data-testid="submit-new-lead"
                >
                  Add Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsManagement;
